import { format, parseISO, differenceInDays, isValid } from "date-fns";
import { Clock, AlertCircle } from "lucide-react";

// Safe date parsing helper
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const safeFormatDate = (dateString, formatStr = "MMM dd, yyyy") => {
  const parsed = safeParseDate(dateString);
  return parsed ? format(parsed, formatStr) : "N/A";
};

export default function UpcomingDeadlines({ orders }) {
  // Get orders sorted by deadline, take only next 5 upcoming (not delivered)
  const upcomingOrders = orders
    .filter((order) => order.current_status !== "Delivered")
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
    .slice(0, 5);

  const getDaysUntilDeadline = (deadline) => {
    const parsed = safeParseDate(deadline);
    if (!parsed) return 0;
    return differenceInDays(parsed, new Date());
  };

  if (upcomingOrders.length === 0) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <h3 className="text-lg font-heading font-bold mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-primary" />
          Upcoming Deadlines
        </h3>
        <p className="text-sm text-muted-foreground text-center py-8">No upcoming deadlines</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-6">
      <h3 className="text-lg font-heading font-bold mb-4 flex items-center" data-testid="upcoming-deadlines-title">
        <Clock className="h-5 w-5 mr-2 text-primary" />
        Upcoming Deadlines
      </h3>
      <div className="space-y-3">
        {upcomingOrders.map((order) => {
          const daysRemaining = getDaysUntilDeadline(order.deadline);
          const isUrgent = daysRemaining <= 3;

          return (
            <div
              key={order.id}
              className={`deadline-item p-4 rounded-md border ${
                isUrgent ? "border-accent bg-accent/5" : "border-border"
              }`}
              data-testid={`deadline-item-${order.id}`}
            >
              <div className="flex items-start gap-3">
                {(order.photos?.[0] || order.photo) && (
                  <img
                    src={order.photos?.[0] || order.photo}
                    alt={order.product_type}
                    className="w-12 h-12 object-cover rounded flex-shrink-0"
                    loading="lazy"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate" data-testid="deadline-client-name">{order.client_name}</p>
                  <p className="text-xs text-muted-foreground truncate" data-testid="deadline-product-type">{order.product_type}</p>
                  <div className="mt-2 flex items-center gap-2">
                    {isUrgent && <AlertCircle className="h-3 w-3 text-accent flex-shrink-0" />}
                    <p
                      className={`text-xs font-medium ${
                        daysRemaining < 0
                          ? "text-red-600"
                          : isUrgent
                          ? "text-accent"
                          : "text-muted-foreground"
                      }`}
                      data-testid="deadline-days-remaining"
                    >
                      {daysRemaining < 0
                        ? `${Math.abs(daysRemaining)} days overdue`
                        : daysRemaining === 0
                        ? "Due today"
                        : `${daysRemaining} days left`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
